<template>
  <div
    :class="[
      $style.config,
      $style.wrap,
      !computedHasTimeline && $style.noTimeline,
    ]"
    data-hero-timeline-v="3.1.0"
  >
    <div :class="$style.mobile">
      <WithBackdrop
        :class="$style.backdrop"
        :backdrop="data?.fields?.backdrop"
        :observe-intersection="false"
        fit="scale"
        eager
      >
        <AspectRatio ratio="16x9" />
        <template v-if="withNav">
          <button
            :class="[$style.button, $style.prev]"
            aria-label="previous slide"
            @click="
              () => {
                direction = 'right'
                emit('request-prev')
              }
            "
          >
            <IconChevronLeft :class="$style.icon" />
          </button>
          <button
            :class="[$style.button, $style.next]"
            aria-label="next slide"
            @click="
              () => {
                direction = 'left'
                emit('request-next')
              }
            "
          >
            <IconChevronRight :class="$style.icon" />
          </button>
        </template>
      </WithBackdrop>
      <div :class="$style.container">
        <div
          v-if="data?.fields?.headerAsset"
          :class="[$style.headerAsset, $style.content]"
        >
          <AspectRatio
            :ratio="`${data?.fields?.headerAsset?.fields?.file?.details?.image?.width}x${data?.fields?.headerAsset?.fields?.file?.details?.image?.height}`"
          >
            <BasePicture
              :src="data?.fields?.headerAsset?.fields?.file?.url || ''"
              loading="eager"
              alt=""
            />
          </AspectRatio>
        </div>

        <div v-if="data?.fields?.copy" :class="$style.text">
          <MarkedParser :md="data?.fields?.copy" :class="$style.copy" />
        </div>

        <Swiper
          v-if="isMounted && computedHasTimeline"
          :class="['swiper', $style.schedules, isSwiperReady && $style.isReady]"
          :slides-per-view="swiperOptions.slidesPerView"
          :slides-per-group="swiperOptions.slidesPerGroup"
          :breakpoints="swiperOptions.breakpoints"
          @swiper="onSwiper"
        >
          <SwiperSlide
            v-for="schedule in data?.fields?.timeline"
            :key="schedule?.sys?.id"
            :class="['swiper-slide', $style.schedule]"
          >
            <HeroTimelineEvent
              v-if="instanceOfEvent(schedule)"
              :data="schedule"
            />
            <HeroTimelineMilestone
              v-if="instanceOfMilestone(schedule)"
              :data="schedule"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
    <div :class="$style.desktop">
      <WithBackdrop
        :class="[$style.backdrop, data?.fields?.tintOverlay && $style.tint]"
        :backdrop="data?.fields?.backdrop"
        :observe-intersection="false"
        fit="thumb"
        eager
      >
        <div :class="$style.content">
          <div
            v-if="data?.fields?.headerAsset"
            :class="[$style.headerAsset, $style.content]"
          >
            <AspectRatio
              :ratio="`${data?.fields?.headerAsset?.fields?.file?.details?.image?.width}x${data?.fields?.headerAsset?.fields?.file?.details?.image?.height}`"
            >
              <BasePicture
                :src="data?.fields?.headerAsset?.fields?.file?.url || ''"
                loading="eager"
                alt=""
              />
            </AspectRatio>
          </div>

          <div v-if="data?.fields?.copy" :class="$style.text">
            <MarkedParser :md="data?.fields?.copy" :class="$style.copy" />
          </div>

          <Swiper
            v-if="data?.fields?.timeline?.length"
            :class="[
              'swiper',
              $style.schedules,
              isSwiperReady && $style.isReady,
            ]"
            :slides-per-view="swiperOptions.slidesPerView"
            :slides-per-group="swiperOptions.slidesPerGroup"
            :breakpoints="swiperOptions.breakpoints"
            @swiper="onSwiper"
          >
            <SwiperSlide
              v-for="schedule in data?.fields?.timeline"
              :key="schedule?.sys?.id"
              :class="['swiper-slide', $style.schedule]"
            >
              <HeroTimelineEvent
                v-if="instanceOfEvent(schedule)"
                :data="schedule"
              />
              <HeroTimelineMilestone
                v-if="instanceOfMilestone(schedule)"
                :data="schedule"
              />
            </SwiperSlide>
          </Swiper>
        </div>
      </WithBackdrop>
      <MarkedParser :md="data?.fields?.append" :class="$style.append" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Swiper as SwiperClass } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'

import type { TypeHeroTimelineWithoutUnresolvableLinksResponse } from '~/types/contentful'

import 'swiper/css'

const emit = defineEmits(['request-prev', 'request-next'])

const props = defineProps<{
  data: TypeHeroTimelineWithoutUnresolvableLinksResponse
  withNav?: boolean
}>()

const direction = ref('')
const isMounted = ref(false)
const isSwiperReady = ref(false)

const swiperOptions = {
  slidesPerView: 1,
  slidesPerGroup: 1,
  breakpoints: {
    480: {
      slidesPerView: 1.5,
    },
    640: {
      slidesPerView: 2.5,
    },
    768: {
      slidesPerView: 3.25,
    },
    1024: {
      slidesPerView: 4.5,
    },
    1250: {
      slidesPerView: 6.5,
    },
    1440: {
      slidesPerView: 7,
    },
  },
}

const computedHasTimeline = computed(() => {
  return props.data?.fields?.timeline && props.data?.fields?.timeline.length > 0
})

const onSwiper = (swiper: SwiperClass) => {
  isSwiperReady.value = true
  swiper.setBreakpoint()

  const initialIndex = props.data?.fields?.activeTimelineIndex || 0
  swiper.slideTo(initialIndex)
}

onMounted(() => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      isMounted.value = true
    })
  })
})
</script>

<style lang="scss" module>
.config {
  --wrap-min-height--base: 465px;
  --wrap-min-height--sm: 560px;
  --wrap-min-height: var(--wrap-min-height--base);

  --container-background--base: var(--black);
  --container-background--sm: unset;
  --container-background: var(--container-background--base);

  --container-padding--base: 1.5em;
  --container-padding--sm: 1em;
  --container-padding--md: 1em;
  --container-padding--lg: 5em;
  --container-padding: var(--container-padding--base);

  --content-max-width: 1440px;

  --tint-overlay: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.75) 30%,
    rgba(0, 0, 0, 0) 54%
  );

  --header-asset-max-width--base: 300px;
  --header-asset-max-width--sm: 380px;
  --header-asset-max-width--md: 470px;
  --header-asset-max-width: var(--header-asset-max-width--base);

  --schedules-max-width--base: 580px;
  --schedules-max-width--md: 1280px;
  --schedules-max-width: var(--schedules-max-width--base);

  --header-asset-margin--base: 0 auto;
  --header-asset-margin--md: 0 auto 0 2em;
  --header-asset-margin: var(--header-asset-margin--base);

  @include media('sm') {
    --wrap-min-height: var(--wrap-min-height--sm);

    --header-asset-max-width: var(--header-asset-max-width--sm);
  }

  @include media('md') {
    --header-asset-max-width: var(--header-asset-max-width--md);
    --header-asset-margin: var(--header-asset-margin--md);
    --schedules-max-width: var(--schedules-max-width--md);
  }
}

.wrap {
  background: var(--black);
  min-height: var(--wrap-min-height);
  overflow: hidden;

  &.noTimeline {
    --wrap-min-height: auto;
  }
}

.mobile {
  display: var(--hidden-sm-and-up);
}

.desktop {
  display: var(--display-sm-and-up);

  .backdrop {
    padding: 120px 0 60px;
  }

  .noTimeline & .backdrop {
    padding: 120px 0;
  }
}

.tint {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--tint-overlay);
    z-index: 0;
  }
}

.container {
  background: var(--container-background);
  padding: var(--container-padding);
}

.content {
  max-width: var(--content-max-width);
  padding: 0 20px;
  margin: 0 auto;
}

.headerAsset {
  max-width: var(--header-asset-max-width);
  margin: var(--header-asset-margin);
  width: 100%;
}

.schedules {
  padding: 2em 0 0 !important; // override swiper
  max-width: var(--schedules-max-width);
  opacity: 0;
  visibility: hidden;

  &.isReady {
    opacity: 1;
    visibility: visible;
    overflow: visible;
  }
}

.schedule {
  color: var(--white);
  font-size: to-rem(17px);
  font-weight: bold;

  min-width: 220px;

  padding: 1.8em 1em 0.25em 0;

  a {
    color: inherit;
  }
}

.copy {
  color: var(--white);
  max-width: 600px;

  p {
    margin: 0 0 24px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--roboto-condensed);
    margin: 0 0 12px;
    line-height: 1;
  }

  h1,
  h2 {
    @include fluid-type(30px, 56px);
  }
}

.text {
  position: relative;
  padding: 0 2em;
  max-width: var(--schedules-max-width);
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: var(--purple-light);
  }
}

.append {
  background: var(--black);
  color: var(--white);
  @include fluid-type(22px, 28px);
  font-family: var(--roboto-condensed);
  font-weight: bold;
  padding: 0.4em;
  text-align: center;
}

.icon {
  height: 17px;
  width: 11px;
}
</style>
